import { supabase } from '../lib/supabase';

/**
 * Realtime Service for Supabase live updates
 * Provides role-filtered subscriptions for shipments, containers, tasks, and events
 */

class RealtimeService {
  constructor() {
    this.channels = new Map();
  }

  /**
   * Subscribe to shipments changes with role-based filtering
   * @param {Function} callback - Function to call when data changes
   * @param {Object} filters - Optional filters (admin sees all, driver sees their tasks only)
   * @returns {Object} Channel subscription
   */
  subscribeToShipments(callback, filters = {}) {
    const channelName = 'shipments-realtime';
    
    // Remove existing channel if any
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shipments',
          ...filters
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to containers changes
   * @param {Function} callback - Function to call when data changes
   * @param {Object} filters - Optional filters
   * @returns {Object} Channel subscription
   */
  subscribeToContainers(callback, filters = {}) {
    const channelName = 'containers-realtime';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'containers',
          ...filters
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to pickup tasks changes
   * @param {Function} callback - Function to call when data changes
   * @param {string} driverUserId - Optional driver user ID for filtering
   * @returns {Object} Channel subscription
   */
  subscribeToPickupTasks(callback, driverUserId = null) {
    const channelName = 'pickup-tasks-realtime';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName);
    }

    const filters = driverUserId
      ? { filter: `driver_user_id=eq.${driverUserId}` }
      : {};

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pickup_tasks',
          ...filters
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to delivery tasks changes
   * @param {Function} callback - Function to call when data changes
   * @param {string} employeeId - Optional employee ID for filtering
   * @returns {Object} Channel subscription
   */
  subscribeToDeliveryTasks(callback, employeeId = null) {
    const channelName = 'delivery-tasks-realtime';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName);
    }

    const filters = employeeId
      ? { filter: `assigned_employee_id=eq.${employeeId}` }
      : {};

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'delivery_tasks',
          ...filters
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to shipment events changes
   * @param {Function} callback - Function to call when data changes
   * @returns {Object} Channel subscription
   */
  subscribeToShipmentEvents(callback) {
    const channelName = 'shipment-events-realtime';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'shipment_events'
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to admin alerts
   * @param {Function} callback - Function to call when new alerts arrive
   * @returns {Object} Channel subscription
   */
  subscribeToAdminAlerts(callback) {
    const channelName = 'admin-alerts-realtime';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_alerts'
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to barrel sales changes
   * @param {Function} callback - Function to call when barrel sales data changes
   * @param {Object} filters - Optional filters
   * @returns {Object} Channel subscription
   */
  subscribeToBarrelSales(callback, filters = {}) {
    const channelName = 'barrel-sales-realtime';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'barrel_sales',
          ...filters
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to barrel deposits changes
   * @param {Function} callback - Function to call when barrel deposits data changes
   * @param {Object} filters - Optional filters
   * @returns {Object} Channel subscription
   */
  subscribeToBarrelDeposits(callback, filters = {}) {
    const channelName = 'barrel-deposits-realtime';
    
    if (this.channels?.has(channelName)) {
      this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'barrel_deposits',
          ...filters
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Unsubscribe from a specific channel
   * @param {string} channelName - Name of the channel to unsubscribe
   */
  unsubscribe(channelName) {
    const channel = this.channels?.get(channelName);
    if (channel) {
      supabase?.removeChannel(channel);
      this.channels?.delete(channelName);
    }
  }

  /**
   * Unsubscribe from all active channels
   */
  unsubscribeAll() {
    this.channels?.forEach((channel, name) => {
      supabase?.removeChannel(channel);
    });
    this.channels?.clear();
  }

  /**
   * Get active channels count
   * @returns {number}
   */
  getActiveChannelsCount() {
    return this.channels?.size;
  }
}

export const realtimeService = new RealtimeService();